/* Whether this person agreed to advertising measurement.
 *
 * The decision is not made here. It is made on kinshiptrees.com, by the banner
 * in `consent.js` in the kinshiptreescom repo, and it travels to the app in a
 * cookie on the shared parent domain. This module reads that cookie, and lets
 * somebody change their mind from inside the app, which the privacy policy
 * promises they can do from any page.
 *
 * THE CONTRACT WITH THE MARKETING SITE.
 * One cookie, `kt_ads_consent`, on `.kinshiptrees.com`, holding exactly
 * `granted` or `denied`. That is the whole of it. Both sides have to agree on
 * the name and the two values, so changing either here means changing
 * consent.js there in the same breath. If they drift, a refusal stops carrying
 * across, and it fails silently: nobody sees an error, the adverts simply start
 * reporting people who said no.
 *
 * WHY A COOKIE AND NOT SOMETHING TIDIER.
 * localStorage is tied to one exact hostname, so a decision made on
 * kinshiptrees.com is invisible at app.kinshiptrees.com. More importantly the
 * event that matters is sent by the API, not the browser, and a parent-domain
 * cookie is attached to every request to /api automatically. The server can
 * therefore check consent itself rather than trusting what a client claims.
 *
 * WHEN THERE IS NO COOKIE AT ALL.
 * Someone who came straight to the app has never passed through the marketing
 * site and so has nothing written down. `initConsent` then applies the same
 * rule the site applies, and it has to be the same rule: outside the UK and
 * EEA measurement is permitted by default, and inside them nothing happens
 * until the person is asked. That second half is not a preference. The privacy
 * policy says we ask first in Europe, so defaulting Europe to on would make the
 * published policy untrue.
 *
 * This is the one piece of logic deliberately duplicated from consent.js on the
 * marketing site, because the two repositories cannot share code. Change the
 * rule in one and you must change it in the other, or the same person gets a
 * different answer depending on which door they came through.
 *
 * ANYTHING OTHER THAN `granted` MEANS NO.
 * Missing, misspelt, truncated, or unreadable because the browser refuses
 * cookies: all of it means no. There is no state that means "probably fine".
 * A browser blocking cookies would have blocked the advertising platform's own
 * cookie too, so nothing is lost that was ever available.
 *
 * THE APP DELIBERATELY DOES NOT ASK.
 * There is no banner here. Anyone arriving from an advert has already passed
 * through kinshiptrees.com and answered there, and people who came straight to
 * the app did not come from an advert worth measuring. Showing a consent
 * banner over somebody's family archive to recover a little attribution is a
 * poor trade. What the app owes them is the ability to change the answer, which
 * is what `setConsent` is for.
 */

const COOKIE = 'kt_ads_consent';
const GRANTED = 'granted';
const DENIED = 'denied';
const ONE_YEAR = 60 * 60 * 24 * 365;

/**
 * Reads the stored decision.
 *
 * Returns `null` when nobody has decided, which callers must treat as a no.
 * `hasConsent` below is the one to reach for unless you specifically need to
 * tell "declined" apart from "never asked".
 */
export function readConsent() {
  if (typeof document === 'undefined') return null;
  try {
    const match = document.cookie.match(/(?:^|;\s*)kt_ads_consent=([^;]*)/);
    const value = match && decodeURIComponent(match[1]);
    return value === GRANTED || value === DENIED ? value : null;
  } catch (e) {
    return null;
  }
}

/**
 * The only question most callers should ask.
 *
 * True exactly when a stored `granted` was found. Everything else, including
 * the browser refusing to keep cookies at all, is false.
 */
export function hasConsent() {
  return readConsent() === GRANTED;
}

/**
 * Records a change of mind made inside the app.
 *
 * Writes the same cookie the marketing site writes, with the same attributes,
 * so that the two are genuinely one value and not two that happen to agree.
 * The domain is only set on the real site: browsers reject
 * `Domain=.kinshiptrees.com` outright on localhost, which would break every
 * local test quietly rather than loudly.
 */
export function setConsent(granted) {
  if (typeof document === 'undefined') return;
  const host = window.location.hostname;
  const onSite = host === 'kinshiptrees.com' || host.endsWith('.kinshiptrees.com');
  try {
    document.cookie =
      `${COOKIE}=${granted ? GRANTED : DENIED}` +
      `; path=/; max-age=${ONE_YEAR}; samesite=lax` +
      (onSite ? '; domain=.kinshiptrees.com' : '') +
      (window.location.protocol === 'https:' ? '; secure' : '');
  } catch (e) {
    /* Nothing to do. An unwritable cookie reads back as absent, which means no,
     * so a refusal that cannot be saved still behaves as a refusal. */
  }
}

/* Europe by the browser's own timezone, which costs no network call and tells
 * nobody that this person exists. It is a guess, so it fails safe: anything
 * European, and anything unreadable, is treated as needing to be asked. Cyprus
 * is the trap, being in the EU while filing its timezone under Asia.
 *
 * Kept character for character the same as the marketing site's copy. */
const EUROPEAN_TZ = /^(Europe\/|Atlantic\/(Azores|Madeira|Canary|Faroe|Reykjavik)|Asia\/(Nicosia|Famagusta))/;

function mustAskFirst() {
  try {
    return EUROPEAN_TZ.test(Intl.DateTimeFormat().resolvedOptions().timeZone || '');
  } catch (e) {
    return true;
  }
}

/**
 * Settles the default for somebody who arrived without a cookie.
 *
 * Call once, on app start. Outside Europe it records `granted`, matching what
 * the marketing site would have recorded had they come through it. Inside
 * Europe it writes nothing at all: an absent cookie already reads as no, and
 * writing `denied` would look like an answer somebody gave when nobody asked
 * them anything.
 *
 * Does nothing when a decision already exists, so it can never overwrite a
 * person's actual choice.
 */
export function initConsent() {
  if (readConsent() !== null) return;
  if (!mustAskFirst()) setConsent(true);
}

/**
 * The advertising platform's own identifiers, if it has left any here.
 *
 * `_fbc` is the valuable one: the pixel writes it when somebody arrives with an
 * `fbclid` on the URL, which only happens on a click from an advert. It names
 * that click, so the platform does not have to work out who a person is in
 * order to credit the advert that brought them. `_fbp` names the browser and
 * helps a little where `_fbc` is absent.
 *
 * Both are set by Meta's pixel on kinshiptrees.com, on the parent domain, so
 * they are readable here in the app as well. They are read rather than written:
 * nothing in this file creates them, and if consent was refused the pixel never
 * ran and there is nothing to find.
 *
 * Returned only when consent is granted. Belt and braces, since the server
 * checks consent too, but the values should not leave the browser at all for
 * somebody who said no.
 */
export function readPlatformIds() {
  if (typeof document === 'undefined' || !hasConsent()) return {};
  const out = {};
  try {
    for (const name of ['_fbc', '_fbp']) {
      const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
      if (match) {
        const value = decodeURIComponent(match[1]);
        // Only the shapes Meta actually writes. A cookie of some other origin
        // sharing the name would be rejected by the platform anyway, and
        // forwarding arbitrary strings from a browser is not a habit worth
        // starting.
        if (/^fb\.[0-9]\.[0-9]+\.[A-Za-z0-9_-]+$/.test(value)) out[name] = value;
      }
    }
  } catch (e) {
    /* Unreadable cookies mean no identifiers, which is the same as none. */
  }
  return out;
}

/** Where someone goes to read what any of this actually means. */
export const POLICY_URL = 'https://kinshiptrees.com/privacy.html#cookies';
